<template>
  <div>
    <LvLoading v-if="isLoading && !_dataList.length" />
    <div
      v-for="(item, itemIndex) in _dataList"
      :key="item.id"
      :style="{ 'margin-left': item.marginLeft }"
    >
      <slot name="content" :item="item" :index="itemIndex">
        <LvText>{{ item.name }}</LvText>
      </slot>
    </div>
    <LvLoading v-if="isLoading && _dataList.length" />
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: Array,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cachedResults: new Map(), // 儲存已計算過的縮排結果
      lastProcessedLength: 0, // 上次處理的資料長度
    };
  },
  computed: {
    _dataList() {
      const currentLength = this.value.length;
      // 如果資料長度沒變，直接返回快取
      if (currentLength === this.lastProcessedLength) {
        return Array.from(this.cachedResults.values());
      }
      // 只處理新增的資料
      return this.$_calculateIncrementalIndentation(this.value);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.cachedResults = new Map();
        this.lastProcessedLength = 0;
      },
      deep: true,
    },
  },
  beforeDestroy() {
    // 清理快取
    this.cachedResults.clear();
    this.cachedResults = null;
  },
  methods: {
    $_calculateIncrementalIndentation(data, options = { indent: 20, unit: 'px' }) {
      if (!Array.isArray(data)) throw new Error('Input must be an array');

      // 建立或更新 id 索引表
      const idMap = new Map(data.map(item => [item.id, item]));

      // 取得新增的項目
      const newItems = data.slice(this.lastProcessedLength);

      // 如果是首次處理或快取被清空
      if (this.cachedResults.size === 0) {
        this.cachedResults = new Map();
        return this.$_processAllItems(data, idMap, options);
      }

      // 處理新增的項目
      newItems.forEach(item => {
        const result = this.$_calculateSingleItemIndentation(
          item,
          data,
          idMap,
          this.cachedResults,
          options,
        );
        this.cachedResults.set(item.id, result);
      });

      // 更新最後處理的長度
      this.lastProcessedLength = data.length;

      // 返回所有結果
      return Array.from(this.cachedResults.values());
    },

    $_processAllItems(data, idMap, options) {
      const indentationMap = new Map();

      data.forEach(item => {
        const result = this.$_calculateSingleItemIndentation(
          item,
          data,
          idMap,
          indentationMap,
          options,
        );
        this.cachedResults.set(item.id, result);
      });

      this.lastProcessedLength = data.length;
      return Array.from(this.cachedResults.values());
    },

    $_calculateSingleItemIndentation(item, fullData, idMap, indentationMap, options) {
      let indent = 0;

      if (item.parentId) {
        if (!idMap.has(item.parentId)) {
          throw new Error(`Invalid parentId: ${item.parentId} for item: ${item.id}`);
        }

        const parent = idMap.get(item.parentId);
        const parentIndex = fullData.indexOf(parent);
        const currentIndex = fullData.indexOf(item);

        if (parentIndex === currentIndex - 1) {
          // 直接相鄰的父子關係
          const parentResult = this.cachedResults.get(item.parentId);
          indent =
            (parentResult?.marginLeft ? parseInt(parentResult.marginLeft) : 0) + options.indent;
        } else {
          // 查找同層級的前一個項目
          const sameParentItems = fullData.filter(d => d.parentId === item.parentId);
          const sameParentPrevious = sameParentItems.find(d => fullData.indexOf(d) < currentIndex);
          if (sameParentPrevious) {
            const previousResult = this.cachedResults.get(sameParentPrevious.id);
            indent =
              previousResult && previousResult.marginLeft ? parseInt(previousResult.marginLeft) : 0;
          } else {
            const parentResult = this.cachedResults.get(item.parentId);
            indent =
              parentResult && parentResult.marginLeft ? parseInt(parentResult.marginLeft) : 0;
          }
        }
      }

      return {
        ...item,
        marginLeft: `${indent}${options.unit}`,
      };
    },
  },
};
</script>
